import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
    IExportPdf,
    IStatementOfAccount,
    IStatementOfAccountFilters,
} from "../types/statement-of-account.types";
import { IApiResponse } from "../../../utils/types/api-response";
import {
    exportPdfs,
    getStatementOfAccount,
} from "../services/statement-of-account.services";
import { handleRemoveEmptyParamFromQueryParams } from "../../../utils/helpers";
import useToast from "../../../hooks/useToast";

export function useFetchStatementOfAccount(
    filters: IStatementOfAccountFilters
) {
    return useQuery<
        IApiResponse<IStatementOfAccount>,
        AxiosError<IApiResponse<IStatementOfAccount>>
    >({
        queryKey: ["account-statement", filters],
        queryFn: () => getStatementOfAccount(filters),
    });
}

export function useGetDownloadJobId(
    filters: IStatementOfAccountFilters,
    isEnabled: boolean
) {
    interface IDownloadResponseData {
        export_job_id: string;
    }
    return useQuery<
        IApiResponse<IDownloadResponseData>,
        AxiosError<IApiResponse<IDownloadResponseData>>
    >({
        queryKey: ["statement-of-account-download-jobId", filters],
        queryFn: () => getStatementOfAccount(filters),
        enabled: isEnabled,
    });
}

export function useExportPdfs(params: IExportPdf, isEnabled: boolean) {
    const toast = useToast();

    return useQuery<Blob, AxiosError<string>>({
        queryKey: [
            "export-pdfs",
            handleRemoveEmptyParamFromQueryParams(params),
        ],
        queryFn: () => exportPdfs(params),
        enabled: isEnabled,
        onSuccess: (data) => {
            const href = window.URL.createObjectURL(data);

            const anchorElement = document.createElement("a");
            anchorElement.href = href;

            document.body.appendChild(anchorElement);
            anchorElement.click();

            document.body.removeChild(anchorElement);
            window.URL.revokeObjectURL(href);
        },
        onError: () => {
            toast.toast({
                mode: "error",
                message: "Could not download statement of account.",
            });
        },
        retry: 2,
        retryDelay: 1000,
    });
}
